import axios from "axios";
import { useNavigate, useParams } from "react-router-dom";
import Apiurl from "../environment/Apiurl";
import { useEffect, useMemo, useState } from "react";

function PatientsHistory() {
  const { patient_id } = useParams();
  const navigate = useNavigate();

  const [patientsHistoryData, setPatientsHistoryData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const [searchQuery, setSearchQuery] = useState("");
  const [pageSize, setPageSize] = useState(5);
  const [currentPage, setCurrentPage] = useState(1);

  const [selectedHistory, setSelectedHistory] = useState(null);
  const [showHistoryModal, setShowHistoryModal] = useState(false);

  // =========================================================
  // FETCH PATIENT PRESCRIPTION HISTORY
  // =========================================================
  const fetchPatientHistory = async () => {
    try {
      setLoading(true);

      const historyResponse = await axios.get(
        `${Apiurl}/prescriptions/patient/${patient_id}`,
      );

      const historyList = Array.isArray(historyResponse.data)
        ? historyResponse.data
        : [];

      const sortedHistory = [...historyList].sort((a, b) => {
        const aDate =
          a?.appointment?.appointment_date || a?.date || "1970-01-01";

        const bDate =
          b?.appointment?.appointment_date || b?.date || "1970-01-01";

        if (aDate === bDate) {
          return String(b?._id || "").localeCompare(String(a?._id || ""));
        }

        return String(bDate).localeCompare(String(aDate));
      });

      setPatientsHistoryData(sortedHistory);
      setError(null);
    } catch (err) {
      console.error("Error fetching patient history:", err);

      setError(
        err?.response?.data?.message ||
          "Failed to load patient prescription history",
      );

      setPatientsHistoryData([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (patient_id) {
      fetchPatientHistory();
    }
  }, [patient_id]);

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: "smooth" });
  }, []);

  // =========================================================
  // GENERAL DISPLAY HELPERS
  // =========================================================
  const isFilled = (value) => {
    if (value === null || value === undefined) return false;

    if (Array.isArray(value)) {
      return value.some((item) => isFilled(item));
    }

    if (typeof value === "object") {
      return Object.values(value).some((item) => isFilled(item));
    }

    if (typeof value === "string") {
      return value.trim() !== "";
    }

    return true;
  };

  const formatLabel = (key) =>
    String(key || "")
      .replace(/_/g, " ")
      .replace(/\b\w/g, (char) => char.toUpperCase());

  const formatValue = (value) => {
    if (Array.isArray(value)) {
      return value.filter((item) => isFilled(item)).join(", ");
    }

    if (typeof value === "boolean") {
      return value ? "Yes" : "No";
    }

    if (!isFilled(value)) {
      return "";
    }

    return String(value);
  };

  const getVisitDate = (history) =>
    history?.appointment?.appointment_date || history?.date || "";

  const getDoctorName = (history) =>
    history?.appointment?.doctor?.doctor_name || "";

  const getAppointmentId = (history) =>
    history?.appointment?.appointment_id || "";

  const getPresentingComplaint = (history) =>
    history?.clinical_assessment?.presenting_complaints ||
    history?.chief_complaint ||
    "";

  const getMedicineNames = (history) =>
    (history?.medicines || [])
      .map((medicine) => medicine?.medicine_name)
      .filter(Boolean);

  const getPatient = () =>
    patientsHistoryData?.[0]?.appointment?.patient || {};

  // =========================================================
  // SEARCH + PAGINATION
  // =========================================================
  const filteredHistoryData = useMemo(() => {
    const query = searchQuery.trim().toLowerCase();

    if (!query) {
      return patientsHistoryData;
    }

    return patientsHistoryData.filter((history) => {
      const searchableText = [
        getVisitDate(history),
        getAppointmentId(history),
        getDoctorName(history),
        history?.appointment?.department,
        getPresentingComplaint(history),
        ...getMedicineNames(history),
      ]
        .filter(Boolean)
        .join(" ")
        .toLowerCase();

      return searchableText.includes(query);
    });
  }, [patientsHistoryData, searchQuery]);

  const totalPages =
    pageSize === "all"
      ? 1
      : Math.max(1, Math.ceil(filteredHistoryData.length / pageSize));

  const paginatedHistoryData =
    pageSize === "all"
      ? filteredHistoryData
      : filteredHistoryData.slice(
          (currentPage - 1) * pageSize,
          currentPage * pageSize,
        );

  const handleSearchChange = (e) => {
    setSearchQuery(e.target.value);
    setCurrentPage(1);
  };

  const handlePageSizeChange = (e) => {
    const value = e.target.value;

    setPageSize(value === "all" ? "all" : Number(value));
    setCurrentPage(1);
  };

  // =========================================================
  // MODAL
  // =========================================================
  const handleViewHistory = (history) => {
    setSelectedHistory(history);
    setShowHistoryModal(true);
  };

  const closeHistoryModal = () => {
    setShowHistoryModal(false);
    setSelectedHistory(null);
  };

  // =========================================================
  // CURRENT PRESCRIPTION DATA TOPICS ONLY
  // =========================================================
  const clinicalSections = [
    {
      title: "Case History",
      fields: [
        ["Presenting Complaints", "presenting_complaints"],
        ["Past History", "past_history"],
        ["Treatment History", "treatment_history"],
        ["Family History", "family_history"],
      ],
    },
    {
      title: "Physical General",
      fields: [
        ["Diet", "diet"],
        ["Appetite", "appetite"],
        ["Thirst", "thirst"],
        ["Bowel Habits", "bowel_habits"],
        ["Urination", "urination"],
        ["Sleep", "sleep"],
        ["Dreams", "dreams"],
        ["Perspiration", "perspiration"],
        ["Habits / Addictions", "habits_addictions"],
        ["Thermal", "thermal"],
        ["Craving", "craving"],
        ["Aversion", "aversion"],
        ["Ailments From", "ailments_from"],
        ["Menstrual History", "menstrual_history"],
      ],
    },
    {
      title: "Physical Examination",
      fields: [
        ["Weight", "weight"],
        ["Height", "height"],
        ["BP", "bp"],
        ["Pulse", "pulse"],
        ["Temperature", "temperature"],
      ],
    },
    {
      title: "Systemic Examination",
      fields: [
        ["CVS", "cvs"],
        ["RS", "rs"],
      ],
    },
    {
      title: "Investigations & Repertorial Totality",
      fields: [
        ["Investigations", "investigations"],
        ["Repertorial Totality", "repertorial_totality"],
      ],
    },
  ];

  const mindFields = [
    ["Usual Mood", "usual_mood"],
    ["Sensitivity to Criticism", "sensitivity_to_criticism"],
    ["Weeping Tendency", "weeping_tendency"],
    ["Consolation", "consolation"],
    ["Memory", "memory"],
    ["Concentration", "concentration"],
    ["Mistakes", "mistakes"],
    ["Cleanliness", "cleanliness"],
    ["Fastidious", "fastidious"],
    ["Disturbed if Out of Place", "disturbed_if_out_of_place"],
    ["Perfectionist", "perfectionist"],
    ["Reaction to Imperfection", "reaction_to_imperfection"],
    ["High Expectations Self", "high_expectations_self"],
    ["High Expectations Others", "high_expectations_others"],
    ["Follows Routine Strictly", "follows_routine_strictly"],
    ["Adaptability to Change", "adaptability_to_change"],
    ["Time Consciousness", "time_consciousness"],
    ["Lifestyle Discipline", "lifestyle_discipline"],
    ["Working Style", "working_style"],
    ["Multitasking", "multitasking"],
    ["Physical Energy", "physical_energy"],
    ["Patience", "patience"],
    ["Reaction While Waiting", "reaction_while_waiting"],
    ["Stubbornness", "stubbornness"],
    ["Changes Opinion Easily", "changes_opinion_easily"],
    ["Forgives / Holds Grudges", "forgives_or_holds_grudges"],
    ["Suppresses / Expresses", "suppresses_or_expresses"],
    ["Overthinks Situations", "overthinks_situations"],
    ["Nature", "nature"],
    ["Needs Control", "needs_control"],
    ["Social Type", "social_type"],
    ["Comfort With Strangers", "comfort_with_strangers"],
    ["Adjusts / Needs Space", "adjusts_or_needs_space"],
    ["Responsibility", "responsibility"],
    ["Initiative Level", "initiative_level"],
    ["Satisfaction", "satisfaction"],
    ["Fears", "fears"],
    ["Specific Phobias", "specific_phobias"],
    ["Anxiety About", "anxiety_about"],
    ["Anxiety Time", "anxiety_time"],
    ["Anticipatory Anxiety", "anticipatory_anxiety"],
    ["Anger Triggers", "anger_triggers"],
    ["Anger Expression", "anger_expression"],
    ["After Anger", "after_anger"],
    ["Past Emotional Shocks", "past_emotional_shocks"],
    ["Effect on Health", "effect_on_health"],
    ["Sleep Position", "sleep_position"],
    ["Dream Themes", "dream_themes"],
    ["Sleep Quality", "sleep_quality"],
    ["Ambition Level", "ambition_level"],
    ["Religious / Spiritual Tendency", "religious_spiritual_tendency"],
    ["Artistic / Creative Interests", "artistic_creative_interests"],
    ["Peculiar Habits", "peculiar_habits"],
  ];

  const getFilledFields = (fields, source) =>
    fields.filter(([, key]) => isFilled(source?.[key]));

  const renderFieldSection = (title, fields, source) => {
    const filledFields = getFilledFields(fields, source);

    if (!filledFields.length) {
      return null;
    }

    return (
      <div className="mb-3">
        <div
          className="fw-bold border-bottom pb-1 mb-2"
          style={{ color: "#018A97" }}
        >
          {title}
        </div>

        <div className="row g-2">
          {filledFields.map(([label, key]) => (
            <div className="col-12 col-md-6" key={key}>
              <div
                className="border rounded px-2 py-2 h-100 d-flex align-items-start justify-content-between gap-3"
                style={{ backgroundColor: "#fafafa" }}
              >
                <div
                  className="small fw-semibold text-secondary"
                  style={{
                    flex: "0 0 42%",
                    minWidth: 0,
                    wordBreak: "break-word",
                  }}
                >
                  {label}
                </div>

                <div
                  className="small text-dark text-end"
                  style={{
                    flex: "1 1 58%",
                    minWidth: 0,
                    whiteSpace: "pre-wrap",
                    wordBreak: "break-word",
                  }}
                >
                  {formatValue(source?.[key])}
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    );
  };

  // =========================================================
  // MEDICINE HELPERS
  // =========================================================
  const getIntakeType = (medicine) => {
    const doseType =
      medicine?.dose_type ||
      medicine?.timing?.fn?.dose_type ||
      medicine?.timing?.an?.dose_type ||
      medicine?.timing?.night?.dose_type ||
      "";

    const formattedDoseType = doseType
      ? doseType.charAt(0).toUpperCase() + doseType.slice(1)
      : "";

    let administration = "";

    if (medicine?.direct) {
      administration = "Direct";
    } else if (medicine?.water) {
      administration = "With Water";
    }

    return [formattedDoseType, administration].filter(Boolean).join(" - ");
  };

  const getTimingText = (timing = {}) => {
    const result = [];

    const slots = [
      ["FN", timing?.fn],
      ["AN", timing?.an],
      ["NIGHT", timing?.night],
    ];

    slots.forEach(([label, slot]) => {
      const meal = [];

      if (
        slot?.before === "1" ||
        slot?.before === true ||
        slot?.before === "true"
      ) {
        meal.push("BF");
      }

      if (
        slot?.after === "1" ||
        slot?.after === true ||
        slot?.after === "true"
      ) {
        meal.push("AF");
      }

      if (meal.length > 0) {
        result.push(`${label}: ${meal.join("/")}`);
      }
    });

    return result.join(", ");
  };

  const selectedMedicines = selectedHistory?.medicines || [];

  const showPriceColumn = selectedMedicines.some(
    (medicine) => Number(medicine?.price || 0) > 0,
  );

  const showDurationColumn = selectedMedicines.some((medicine) =>
    isFilled(medicine?.duration),
  );

  const showIntakeColumn = selectedMedicines.some((medicine) =>
    isFilled(getIntakeType(medicine)),
  );

  const showTimingColumn = selectedMedicines.some((medicine) =>
    isFilled(getTimingText(medicine?.timing)),
  );

  const showSosColumn = selectedMedicines.some(
    (medicine) => medicine?.sos === true,
  );

  // =========================================================
  // PAGE STATES
  // =========================================================
  if (loading) {
    return (
      <div className="p-3 text-center">
        <div className="spinner-border text-primary" role="status">
          <span className="visually-hidden">Loading...</span>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="p-3">
        <div className="alert alert-danger">
          {error}

          <button
            className="btn btn-sm btn-outline-danger ms-2"
            onClick={fetchPatientHistory}
          >
            Retry
          </button>
        </div>
      </div>
    );
  }

  const patient = getPatient();

  return (
    <>
      <div className="p-2">
        {/* =====================================================
            PATIENT DETAILS
        ===================================================== */}
        <div className="card mb-2">
          <div
            className="card-header d-flex flex-wrap align-items-center justify-content-between gap-2"
            style={{ backgroundColor: "#01AEBE", color: "white" }}
          >
            <h6 className="mb-0">Patient Visit History</h6>

            <button
              type="button"
              className="btn btn-sm btn-light"
              onClick={() => navigate(-1)}
            >
              Back
            </button>
          </div>

          <div className="card-body py-2">
            <div className="row g-2 small">
              <div className="col-12 col-md-4">
                <span className="fw-semibold text-secondary">Patient ID:</span>{" "}
                {patient?.patient_id || patient_id || "N/A"}
              </div>

              <div className="col-12 col-md-4">
                <span className="fw-semibold text-secondary">Patient:</span>{" "}
                {patient?.first_name || "N/A"} {patient?.last_name || ""}
              </div>

              <div className="col-12 col-md-4">
                <span className="fw-semibold text-secondary">Age / Sex:</span>{" "}
                {patient?.age || "N/A"} / {patient?.gender || "N/A"}
              </div>
            </div>
          </div>
        </div>

        {/* =====================================================
            SEARCH / PAGE SIZE
        ===================================================== */}
        <div className="card">
          <div className="d-flex flex-wrap align-items-center justify-content-between gap-2 p-2 border-bottom bg-light">
            <div style={{ maxWidth: "320px", width: "100%" }}>
              <input
                type="text"
                className="form-control form-control-sm"
                placeholder="Search date, doctor, complaint, medicine..."
                value={searchQuery}
                onChange={handleSearchChange}
              />
            </div>

            <div className="d-flex align-items-center gap-2">
              <span className="badge bg-light text-dark border">
                {filteredHistoryData.length} Visit
                {filteredHistoryData.length === 1 ? "" : "s"}
              </span>

              <label
                className="small fw-semibold mb-0"
                style={{ whiteSpace: "nowrap" }}
              >
                Show:
              </label>

              <select
                className="form-select form-select-sm"
                style={{ width: "auto" }}
                value={pageSize}
                onChange={handlePageSizeChange}
              >
                <option value={5}>5</option>
                <option value={10}>10</option>
                <option value={20}>20</option>
                <option value={50}>50</option>
                <option value="all">All</option>
              </select>
            </div>
          </div>

          {/* =====================================================
              PREVIOUS VISIT TABLE
          ===================================================== */}
          <div className="table-responsive">
            <table className="table table-sm table-hover align-middle mb-0">
              <thead
                style={{
                  backgroundColor: "#01AEBE",
                  color: "white",
                }}
              >
                <tr>
                  <th className="text-center" style={{ width: "60px" }}>
                    S.No
                  </th>

                  <th style={{ minWidth: "110px" }}>Visit Date</th>

                  <th style={{ minWidth: "130px" }}>Appointment ID</th>

                  <th style={{ minWidth: "150px" }}>Doctor</th>

                  <th style={{ minWidth: "200px" }}>Complaint</th>

                  <th style={{ minWidth: "220px" }}>Medicines</th>

                  <th
                    className="text-center"
                    style={{ width: "90px", minWidth: "90px" }}
                  >
                    Action
                  </th>
                </tr>
              </thead>

              <tbody>
                {patientsHistoryData.length === 0 ? (
                  <tr>
                    <td colSpan={7} className="text-center text-muted py-4">
                      No previous prescription visit found for this patient.
                    </td>
                  </tr>
                ) : filteredHistoryData.length === 0 ? (
                  <tr>
                    <td colSpan={7} className="text-center text-muted py-4">
                      No matching visit found.
                    </td>
                  </tr>
                ) : (
                  paginatedHistoryData.map((history, sliceIndex) => {
                    const rowIndex =
                      pageSize === "all"
                        ? sliceIndex
                        : (currentPage - 1) * pageSize + sliceIndex;

                    const medicineNames = getMedicineNames(history);

                    const visibleMedicineNames = medicineNames.slice(0, 3);

                    const complaint = getPresentingComplaint(history);

                    return (
                      <tr key={history?._id || rowIndex}>
                        <td className="text-center">{rowIndex + 1}</td>

                        <td className="fw-semibold">
                          {getVisitDate(history) || "N/A"}
                        </td>

                        <td>{getAppointmentId(history) || "N/A"}</td>

                        <td>{getDoctorName(history) || "N/A"}</td>

                        <td>
                          <div
                            className="text-truncate"
                            style={{ maxWidth: "240px" }}
                            title={complaint}
                          >
                            {complaint || "N/A"}
                          </div>
                        </td>

                        <td>
                          <div
                            className="text-truncate"
                            style={{ maxWidth: "260px" }}
                            title={medicineNames.join(", ")}
                          >
                            {visibleMedicineNames.length > 0
                              ? visibleMedicineNames.join(", ")
                              : "N/A"}

                            {medicineNames.length > 3
                              ? ` +${medicineNames.length - 3}`
                              : ""}
                          </div>
                        </td>

                        <td className="text-center">
                          <button
                            type="button"
                            className="btn btn-sm btn-outline-primary"
                            title="View Visit Details"
                            onClick={() => handleViewHistory(history)}
                            aria-label="View Visit Details"
                          >
                            <i className="bi bi-eye"></i>
                          </button>
                        </td>
                      </tr>
                    );
                  })
                )}
              </tbody>
            </table>
          </div>

          {/* =====================================================
              PAGINATION
          ===================================================== */}
          {pageSize !== "all" &&
            filteredHistoryData.length > 0 &&
            totalPages > 1 && (
              <div className="d-flex flex-wrap align-items-center justify-content-between gap-2 p-2 border-top bg-light">
                <div className="small text-muted">
                  Page {currentPage} of {totalPages}
                </div>

                <div className="btn-group btn-group-sm">
                  <button
                    type="button"
                    className="btn btn-outline-secondary"
                    disabled={currentPage <= 1}
                    onClick={() =>
                      setCurrentPage((prev) => Math.max(1, prev - 1))
                    }
                  >
                    Previous
                  </button>

                  <button
                    type="button"
                    className="btn btn-outline-secondary"
                    disabled={currentPage >= totalPages}
                    onClick={() =>
                      setCurrentPage((prev) =>
                        Math.min(totalPages, prev + 1),
                      )
                    }
                  >
                    Next
                  </button>
                </div>
              </div>
            )}
        </div>
      </div>

      {/* =========================================================
          VIEW HISTORY MODAL
      ========================================================= */}
      <div
        className={`modal fade ${showHistoryModal ? "show" : ""}`}
        style={{
          display: showHistoryModal ? "block" : "none",
          backgroundColor: "rgba(0,0,0,0.5)",
        }}
        tabIndex="-1"
        aria-hidden={!showHistoryModal}
      >
        <div
          className="modal-dialog modal-xl modal-dialog-centered modal-dialog-scrollable"
          style={{ maxWidth: "95vw" }}
        >
          <div className="modal-content" style={{ maxHeight: "92vh" }}>
            {/* ================= HEADER ================= */}
            <div className="modal-header py-2">
              <div>
                <h6 className="modal-title mb-0">Previous Visit Details</h6>

                <small className="text-muted">
                  {selectedHistory
                    ? getVisitDate(selectedHistory) || "Visit"
                    : ""}
                </small>
              </div>

              <button
                type="button"
                className="btn-close"
                onClick={closeHistoryModal}
                aria-label="Close"
              ></button>
            </div>

            {/* ================= BODY ================= */}
            <div className="modal-body p-2">
              {selectedHistory && (
                <>
                  {/* ================= VISIT DETAILS ================= */}
                  <div className="border rounded bg-light px-2 py-2 mb-3">
                    <div className="row g-2 small">
                      {isFilled(getVisitDate(selectedHistory)) && (
                        <div className="col-12 col-md-3">
                          <span className="fw-semibold">Visit Date:</span>{" "}
                          {getVisitDate(selectedHistory)}
                        </div>
                      )}

                      {isFilled(getAppointmentId(selectedHistory)) && (
                        <div className="col-12 col-md-3">
                          <span className="fw-semibold">Appointment ID:</span>{" "}
                          {getAppointmentId(selectedHistory)}
                        </div>
                      )}

                      {isFilled(getDoctorName(selectedHistory)) && (
                        <div className="col-12 col-md-3">
                          <span className="fw-semibold">Doctor:</span>{" "}
                          {getDoctorName(selectedHistory)}
                        </div>
                      )}

                      {isFilled(
                        selectedHistory?.appointment?.department,
                      ) && (
                        <div className="col-12 col-md-3">
                          <span className="fw-semibold">Department:</span>{" "}
                          {selectedHistory.appointment.department}
                        </div>
                      )}

                      {isFilled(selectedHistory?.next_visit_date) && (
                        <div className="col-12 col-md-3">
                          <span className="fw-semibold">Next Visit:</span>{" "}
                          {selectedHistory.next_visit_date}
                        </div>
                      )}
                    </div>
                  </div>

                  {/* ================= QUICK VITALS ================= */}
                  {[
                    ["Weight", selectedHistory?.prescription_weight],
                    ["BP", selectedHistory?.prescription_bp],
                    ["Pulse", selectedHistory?.prescription_pulse],
                    [
                      "Temperature",
                      selectedHistory?.prescription_temperature,
                    ],
                  ].some(([, value]) => isFilled(value)) && (
                    <div className="border rounded px-2 py-2 mb-3">
                      <div className="row g-2 small">
                        {[
                          ["Weight", selectedHistory?.prescription_weight],
                          ["BP", selectedHistory?.prescription_bp],
                          ["Pulse", selectedHistory?.prescription_pulse],
                          [
                            "Temperature",
                            selectedHistory?.prescription_temperature,
                          ],
                        ]
                          .filter(([, value]) => isFilled(value))
                          .map(([label, value]) => (
                            <div className="col-6 col-md-3" key={label}>
                              <span className="fw-semibold">{label}:</span>{" "}
                              {value}
                            </div>
                          ))}
                      </div>
                    </div>
                  )}

                  {/* ================= CLINICAL ASSESSMENT ================= */}
                  {(() => {
                    const assessment =
                      selectedHistory?.clinical_assessment || {};

                    const mind = assessment?.mind || {};

                    const hasClinicalData =
                      clinicalSections.some(
                        (section) =>
                          getFilledFields(section.fields, assessment).length >
                          0,
                      ) ||
                      isFilled(assessment?.case_study) ||
                      getFilledFields(mindFields, mind).length > 0;

                    if (!hasClinicalData) {
                      return null;
                    }

                    return (
                      <div className="mb-3">
                        <div
                          className="fw-bold mb-2"
                          style={{
                            color: "#018A97",
                            fontSize: "16px",
                          }}
                        >
                          Clinical Assessment
                        </div>

                        {renderFieldSection(
                          clinicalSections[0].title,
                          clinicalSections[0].fields,
                          assessment,
                        )}

                        {isFilled(assessment?.case_study) && (
                          <div className="mb-3">
                            <div
                              className="border rounded px-2 py-2 small d-flex align-items-start justify-content-between gap-3"
                              style={{ backgroundColor: "#fafafa" }}
                            >
                              <div
                                className="fw-semibold text-secondary"
                                style={{
                                  flex: "0 0 42%",
                                  minWidth: 0,
                                  wordBreak: "break-word",
                                }}
                              >
                                Case Study
                              </div>

                              <div
                                className="text-dark text-end"
                                style={{
                                  flex: "1 1 58%",
                                  minWidth: 0,
                                  whiteSpace: "pre-wrap",
                                  wordBreak: "break-word",
                                }}
                              >
                                {assessment.case_study}
                              </div>
                            </div>
                          </div>
                        )}

                        {renderFieldSection(
                          clinicalSections[1].title,
                          clinicalSections[1].fields,
                          assessment,
                        )}

                        {renderFieldSection("Mind", mindFields, mind)}

                        {clinicalSections.slice(2).map((section) => (
                          <div key={section.title}>
                            {renderFieldSection(
                              section.title,
                              section.fields,
                              assessment,
                            )}
                          </div>
                        ))}
                      </div>
                    );
                  })()}

                  {/* ================= MEDICINES ================= */}
                  {selectedMedicines.length > 0 && (
                    <div className="mb-3">
                      <div
                        className="fw-bold border-bottom pb-1 mb-2"
                        style={{ color: "#018A97" }}
                      >
                        Medicines
                      </div>

                      <div className="table-responsive">
                        <table className="table table-sm table-bordered align-middle mb-0">
                          <thead
                            style={{
                              backgroundColor: "#01AEBE",
                              color: "white",
                            }}
                          >
                            <tr>
                              <th
                                className="text-center"
                                style={{ width: "55px" }}
                              >
                                S.No
                              </th>

                              <th style={{ minWidth: "180px" }}>
                                Medicine
                              </th>

                              {showPriceColumn && (
                                <th style={{ minWidth: "90px" }}>Price</th>
                              )}

                              {showDurationColumn && (
                                <th style={{ minWidth: "170px" }}>
                                  Duration / Instruction
                                </th>
                              )}

                              {showIntakeColumn && (
                                <th style={{ minWidth: "150px" }}>
                                  Intake Type
                                </th>
                              )}

                              {showTimingColumn && (
                                <th style={{ minWidth: "210px" }}>
                                  FN / AN / NIGHT
                                </th>
                              )}

                              {showSosColumn && (
                                <th
                                  className="text-center"
                                  style={{ width: "70px" }}
                                >
                                  SOS
                                </th>
                              )}
                            </tr>
                          </thead>

                          <tbody>
                            {selectedMedicines.map((medicine, index) => (
                              <tr key={medicine?._id || index}>
                                <td className="text-center">{index + 1}</td>

                                <td>{medicine?.medicine_name || ""}</td>

                                {showPriceColumn && (
                                  <td>
                                    {Number(medicine?.price || 0) > 0
                                      ? `₹${medicine.price}`
                                      : ""}
                                  </td>
                                )}

                                {showDurationColumn && (
                                  <td>{medicine?.duration || ""}</td>
                                )}

                                {showIntakeColumn && (
                                  <td>{getIntakeType(medicine)}</td>
                                )}

                                {showTimingColumn && (
                                  <td>{getTimingText(medicine?.timing)}</td>
                                )}

                                {showSosColumn && (
                                  <td className="text-center">
                                    {medicine?.sos ? "Yes" : ""}
                                  </td>
                                )}
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    </div>
                  )}

                  {/* ================= GENERAL INSTRUCTION ================= */}
                  {isFilled(selectedHistory?.instructions) && (
                    <div className="mb-2">
                      <div
                        className="border rounded px-2 py-2 small d-flex align-items-start justify-content-between gap-3"
                        style={{ backgroundColor: "#fafafa" }}
                      >
                        <div
                          className="fw-semibold text-secondary"
                          style={{
                            flex: "0 0 42%",
                            minWidth: 0,
                            wordBreak: "break-word",
                          }}
                        >
                          Instructions
                        </div>

                        <div
                          className="text-dark text-end"
                          style={{
                            flex: "1 1 58%",
                            minWidth: 0,
                            whiteSpace: "pre-wrap",
                            wordBreak: "break-word",
                          }}
                        >
                          {selectedHistory.instructions}
                        </div>
                      </div>
                    </div>
                  )}
                </>
              )}
            </div>

            {/* ================= FOOTER ================= */}
            <div className="modal-footer py-2">
              <button
                type="button"
                className="btn btn-sm btn-secondary"
                onClick={closeHistoryModal}
              >
                Close
              </button>
            </div>
          </div>
        </div>
      </div>
    </>
  );
}

export default PatientsHistory;